import React, { useState } from 'react';

import { useMuxSpace, useTranslation, useAppDispatch, useGAEventCommonProps } from 'client/hooks';
import { setIsShowFooterPopUpOverlay, setIsShowJoinToSpacePopup } from 'client/redux/ui';
import { analytics } from 'client/utils/analytics';
import wakeLock from 'client/utils/wake-lock';
import { USER_ROLES } from 'constants/index';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';

import TransitionComponent from '../../TransitionComponent';
import PopupWrapper from '../PopupWrapper';
import Button from '../../Button';
import Icon from '../../Icon';

import css from './JoinToSpacePopup.module.scss';

interface Props {
  visible: boolean;
  canPublishAudio: boolean;
  userRole: USER_ROLES;
}

const JoinToSpacePopup: React.FC<Props> = ({ visible, userRole, canPublishAudio }) => {
  const { translate } = useTranslation('popUps');
  const [isUnmuteNeeded, setIsUnmuteNeeded] = useState(false);
  const { state, dispatch: liveStreamDispatch } = useLiveStreamContext();
  const [error, setError] = useState('');
  const { join, isBusy } = useMuxSpace();
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();

  const onSubmit = async (needToMuteMic = true) => {
    wakeLock.request();
    setError('');

    try {
      if (state.startAudioIssue && state.space) {
        console.info('start audio manually');
        state.space.startAudio().then(() => liveStreamDispatch({ type: 'SET_START_AUDIO_ISSUE', payload: false }));
      } else {
        const joinResult = await join({
          canPublish: canPublishAudio,
          micInitialState: needToMuteMic ? 'muted' : 'unmuted',
        });

        if (joinResult?.CODE === 'GET_TOKEN_REQUEST_ERROR') {
          throw new Error(joinResult?.CODE);
        }

        if (isUnmuteNeeded && userRole === USER_ROLES.LISTENER) {
          analytics.gtag.event('audio_chat_content_click', {
            ...common,
            text_clicked: translate('listenerActivateMicrophonePopUp.muteBtn'),
          });
        }

        if (userRole !== USER_ROLES.LISTENER) {
          analytics.gtag.event('audio_chat_content_click', {
            ...common,
            text_clicked:
              isBusy && !error ? `${translate('joinRoomPopUp.loading')}...` : translate('joinRoomPopUp.join'),
          });
        }
      }

      dispatch(setIsShowJoinToSpacePopup(false));
      dispatch(setIsShowFooterPopUpOverlay(false));
    } catch (e) {
      const message = (e as Error).message;

      if (message.includes('duplicate participant')) {
        setError(translate('joinRoomPopUp.duplicateError'));
      }
      if (message === 'GET_TOKEN_REQUEST_ERROR') {
        setError(message);
      }
    }
  };

  const onSubmitListener = async () => {
    const needToMuteMic = false;
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('listenerActivateMicrophonePopUp.activateBtn'),
    });

    await onSubmit(needToMuteMic);

    setIsUnmuteNeeded(true);
  };

  const renderListenerContent = () => {
    if (userRole === USER_ROLES.LISTENER && canPublishAudio) {
      return (
        <>
          <div className={css.icon}>
            <Icon type="red_microphone" />
          </div>
          <div className={css.title}>{translate('listenerActivateMicrophonePopUp.title')}</div>
          <div className={css.text}>{translate('listenerActivateMicrophonePopUp.text')}</div>

          {error === 'GET_TOKEN_REQUEST_ERROR' && (
            <div className={css.error}>{translate(`joinRoomPopUp.${error}`)}</div>
          )}

          <Button
            className={css.activateBtn}
            disabled={isBusy || (!!error && error !== 'GET_TOKEN_REQUEST_ERROR')}
            onClickHandler={onSubmitListener}
            label={translate('listenerActivateMicrophonePopUp.activateBtn')}
          />

          <Button
            isUnderline
            isCustom
            disabled={isBusy || (!!error && error !== 'GET_TOKEN_REQUEST_ERROR')}
            className={css.muteBtn}
            onClickHandler={onSubmit}
            label={translate('listenerActivateMicrophonePopUp.muteBtn')}
          />
        </>
      );
    }

    return (
      <>
        <div className={css.icon}>
          <Icon type={`${userRole === USER_ROLES.LISTENER ? 'sound' : 'red_microphone'}`} />
        </div>
        <div className={css.title}>
          <span>
            {translate(`joinRoomPopUp.${userRole === USER_ROLES.LISTENER ? 'welcomeToListener' : 'welcome'}`)}
          </span>
        </div>

        {error && (
          <div className={css.error}>
            <span>{error === 'GET_TOKEN_REQUEST_ERROR' ? translate(`joinRoomPopUp.${error}`) : error}</span>
          </div>
        )}

        <Button
          className={css.agreeBtn}
          onClickHandler={onSubmit}
          disabled={isBusy || (!!error && error !== 'GET_TOKEN_REQUEST_ERROR')}
          label={isBusy && !error ? `${translate('joinRoomPopUp.loading')}...` : translate('joinRoomPopUp.join')}
        />
      </>
    );
  };

  return (
    <TransitionComponent type="opacity" visible={visible}>
      <PopupWrapper withCloseIcon={false} className="biggerZIndex">
        <div className={css.container}>{renderListenerContent()}</div>
      </PopupWrapper>
    </TransitionComponent>
  );
};

export default JoinToSpacePopup;
